import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Importing icons for better UX
import Select from "react-select";
import { FaRegSquare, FaCheckSquare } from "react-icons/fa";
import secureStorage from "../utils/secureStorage";
import { validateToken } from "../tokenValidation";

const consentTexts = [
  `I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA) for the sole purpose of creation of ABHA number. I understand that my ABHA number can be used and shared for purposes as may be notified by ABDM from time to time including provision of healthcare services. Further, I am aware that my personal identifiable information (Name, Address, Age, Date of Birth, Gender and Photograph) may be made available to the entities working in the National Digital Health Ecosystem (NDHE) which inter alia includes stakeholders and entities such as healthcare professionals (e.g. doctors), facilities (e.g. hospitals, laboratories) and data fiduciaries (e.g. health programmes), which are registered with or linked to the Ayushman Bharat Digital Mission (ABDM), and various processes there under.`,
  `I authorize NHA to use my Aadhaar number for performing Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose. I understand that UIDAI will share my e-KYC details, or response of “Yes” with NHA upon successful authentication.`,
  `I consent to usage of my ABHA address and ABHA number for linking of my legacy (past) health records and those which will be generated during this encounter.`,
  `I authorize the sharing of all my health records with healthcare provider(s) for the purpose of providing healthcare services to me during this encounter.`,
  `I consent to the anonymization and subsequent use of my health records for public health purposes.`,
  `I, (name of healthcare worker – depending on the username used for logging in into the system), confirm that I have duly informed and explained the beneficiary of the contents of consent for aforementioned purposes.`,
  `I, (beneficiary name), have been explained about the consent as stated above and hereby provide my consent for the aforementioned purposes.`,
];
const ForgotAbha = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const inputRefs = useRef([]);
  const MAX_RESEND_ATTEMPTS = 2;
  const [activeTab, setActiveTab] = useState("aadhaar");
  const [aadhaarNumber, setAadhaarNumber] = useState(["", "", ""]);
  const [mobileNumber, setMobileNumber] = useState("");
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [isCaptchaCorrect, setIsCaptchaCorrect] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [txnId, setTxnId] = useState(null);
  const [token, setToken] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [otpMessage, setOtpMessage] = useState("");
  const [aadhaarResponse, setAadhaarResponse] = useState(null);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [timer, setTimer] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [isAadhaarMasked, setIsAadhaarMasked] = useState(true); // New state for masking
  const navigate = useNavigate();
  const [showNoAbhaPopup, setShowNoAbhaPopup] = useState(false);
 const beneficiaryRef = useRef(null);
  const handleOtpChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const cancelNoAbhaPopup = () => {
    setShowNoAbhaPopup(false);
    resetFields();
  };

  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    resetFields();
  };

  const resetFields = () => {
    setAadhaarNumber(["", "", ""]);
    setMobileNumber("");
    setCaptcha(generateCaptcha());
    setCaptchaAnswer("");
    setIsCaptchaCorrect(false);
    setIsAgreed(false);
    setOtp(Array(6).fill(""));
    setTxnId(null);
    setShowOtpPopup(false);
    setIsAadhaarMasked(true); // Reset masking when switching tabs
  };

  const handleCaptchaChange = (value) => {
    setCaptchaAnswer(value);
    setIsCaptchaCorrect(value === captcha.answer);
  };

  const sendOtp = async (isResend = false) => {
    if (isResend && resendAttempts >= 2) {
      toast.error("You have reached the maximum number of resend attempts.");
      return;
    }

    // Validation logic remains the same
    if (
      activeTab === "aadhaar" &&
      aadhaarNumber.some((num) => num.length < 4)
    ) {
      toast.error("Please enter a valid Aadhaar number.");
      return;
    }

    if (
      activeTab === "mobile" &&
      (mobileNumber.length !== 10 || !/^[6-9]\d{9}$/.test(mobileNumber))
    ) {
      toast.error(
        "Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9."
      );
      return;
    }

    if (activeTab === "aadhaar" && !isAgreed) {
      toast.error("You must agree to the Terms and Conditions.");
      return;
    }

    if (!isCaptchaCorrect) {
      toast.error("Captcha is incorrect. Please try again.");
      return;
    }

    setLoading(true);
    setOtp(Array(6).fill(""));
    try {
      let url = "";
      let method = "GET"; // Default method
      const clinicId = secureStorage.get("clinic");
      if (activeTab === "aadhaar") {
        const aadhaarJoined = aadhaarNumber.join("");
        url = `${API_URL}abdm/login-send-otp?loginHint=${aadhaarJoined}&loginType=aadhaar-verify`;
        method = "POST";
      } else if (activeTab === "mobile") {
        url = `${API_URL}abdm/send-communication-otp?mobileNumber=${mobileNumber}`;
        method = "GET";
      }
      setProfiles([]);
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "X-HIP-ID": clinicId,
        },
      });

      const data = await response.json();

      if (response.ok && data.status === 200) {
        setTxnId(data.data.txnId);
        setShowOtpPopup(true);
        if (data.data.message) {
          setOtpMessage(data.data.message);
        } else {
          setOtpMessage("");
        }
        toast.success(data.data.message || "OTP sent successfully!");
        setTimer(60);
        setIsResendDisabled(true);
        if (isResend) {
          setResendAttempts((prev) => prev + 1);
          setTimer(60);
          setIsResendDisabled(true);
        }
      } else {
        if (data && data.data && data.data === "Invalid LoginId") {
          toast.error(
            "Invalid Aadhaar number. Please enter a correct Aadhaar number."
          );
        } else if (data && data.data) {
          toast.error(
            data.data || "Failed to resend OTP. Please try again later."
          );
        } else {
          toast.error("Failed to resend OTP. Please try again later.");
        }
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error(
        "An error occurred while sending OTP. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = () => sendOtp(false);
  const handleResendOtp = () => sendOtp(true);

  useEffect(() => {
    validateToken(navigate);
  }, [navigate]);

  useEffect(() => {
    if (timer > 0) {
      const id = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(id);
    } else if (isResendDisabled) {
      // cooldown over
      setIsResendDisabled(false);
    }
  }, [timer, isResendDisabled]);

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP.");
      return;
    }

    setLoading(true);

    try {
      const clinicId = secureStorage.get("clinic");
      const orgId = secureStorage.get("orgId");
      const url = `${API_URL}abdm/login-verify-otp?orgId=${orgId}`;
      const payload = {
        txnId: txnId,
        otp: otp.join(""),
        loginType: activeTab === "aadhaar" ? "aadhaar-verify" : "mobile-verify",
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-HIP-ID": clinicId,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        setShowOtpPopup(false);
        if (data.status === 200) {
          setAadhaarResponse(data.data);
          const userData = { ...data.data };
          console.log("Recovered data : ", userData);

          if (
            Array.isArray(userData.existingProfiles) &&
            userData.existingProfiles.length > 0
          ) {
            navigate("/profile-comparison", {
              state: {
                abhaProfile: userData.abhaProfile,
                existingProfiles: userData.existingProfiles,
              },
            });
          } else {
            navigate("/patient-profile", {
              state: {
                abhaAddress: userData.abhaProfile?.preferredAbhaAddress,
              },
            });
          }
        } else {
          toast.error(data.data || "Invalid OTP. Please try again.");
        }
      } else {
        if (
          data.data &&
          data.data === "No ABHA user registered with this Aadhaar number."
        ) {
          setShowOtpPopup(false);
          setShowNoAbhaPopup(true);
          return;
        }

        toast.error(data.data || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error(
        "An error occurred while verifying OTP. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyMobileOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP.");
      return;
    }

    setLoading(true);
    try {
      const clinicId = secureStorage.get("clinic");
      const url = `${API_URL}abdm/verify-communication-otp`;
      const payload = { txnId, otp: otp.join("") };

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-HIP-ID": clinicId },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.status === 200) {
        setToken(data.data.token);
        setTxnId(data.data.txnId);
        setProfiles(data.data.accounts);
        toast.success("OTP verified successfully!");
      } else {
        toast.error(data.data || "Failed to verify OTP.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("Error occurred while verifying OTP.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileDetails = async (abhaNumber) => {
    setLoading(true);
    try {
      const clinicId = secureStorage.get("clinic");
      const url = `${API_URL}abdm/get-communication-profile`;
      const payload = {
        txnId,
        token,
        abhaNumber,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-HIP-ID": clinicId },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.status === 200) {
        console.log("Recovered data : ", data.data);

        if (
          Array.isArray(data.data.existingProfiles) &&
          data.data.existingProfiles.length > 0
        ) {
          navigate("/profile-comparison", {
            state: {
              abhaProfile: data.data.abhaProfile,
              existingProfiles: data.data.existingProfiles,
            },
          });
        } else {
          navigate("/patient-profile", {
            state: { abhaAddress: data.data.abhaProfile?.preferredAbhaAddress },
          });
        }
        // const role = secureStorage.get("role");
        // console.log("Redirecting based on role:", role);

        // if (role === "HOSPITAL") {
        //   const userData = { ...data.data };
        //   console.log("User Data before storing", userData);
        //   toast.success("ABHA verified successfully!");
        //   navigate("/patient-profile", {
        //     state: { abhaNumber: userData?.abhaNumber },
        //   });
        // } else {
        //   console.log("Profile Details:", data.data);
        //   setAadhaarResponse(data.data);
        //   const userData = { ...data.data };

        //   localStorage.setItem("user", JSON.stringify(userData)); // Store user data
        //   toast.success("Profile fetched successfully!");
        //   setShowOtpPopup(false);
        //   navigate("/profile", { state: { aadhaarResponse: userData } });
        // }
      } else {
        toast.error(data.message || "Failed to fetch profile.");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Error occurred while fetching profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-10">
        <h2 className="text-2xl font-bold text-teal-600 mb-8 text-center">
          Fetch ABHA Details
        </h2>
        <p className="text-gray-600 mb-6 text-center">
          You can fetch your ABHA number using your Aadhaar number or registered
          mobile number.
        </p>

        {/* Tabs */}
        <div className="flex mb-8 border-b-2">
          <button
            className={`flex-1 text-center py-2 font-semibold ${
              activeTab === "aadhaar"
                ? "border-b-4 border-teal-600 text-black"
                : "text-gray-500"
            }`}
            onClick={() => handleTabSwitch("aadhaar")}
          >
            Aadhaar Number
          </button>
          <button
            className={`flex-1 text-center py-2 font-semibold ${
              activeTab === "mobile"
                ? "border-b-4 border-teal-600 text-black"
                : "text-gray-500"
            }`}
            onClick={() => handleTabSwitch("mobile")}
          >
            Mobile Number
          </button>
        </div>

        {/* Aadhaar Form */}
        {activeTab === "aadhaar" && (
          <>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Aadhaar Number<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="flex flex-col">
                <div className="flex space-x-2 mb-2">
                  {aadhaarNumber.map((num, index) => (
                    <input
                      key={index}
                      type={isAadhaarMasked ? "password" : "text"}
                      value={num}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 4);
                        const newAadhaar = [...aadhaarNumber];
                        newAadhaar[index] = value;
                        setAadhaarNumber(newAadhaar);
                        if (
                          value.length === 4 &&
                          index < aadhaarNumber.length - 1
                        ) {
                          document
                            .getElementById(`aadhaar-input-${index + 1}`)
                            .focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Backspace" &&
                          num.length === 0 &&
                          index > 0
                        ) {
                          document
                            .getElementById(`aadhaar-input-${index - 1}`)
                            .focus();
                        }
                      }}
                      id={`aadhaar-input-${index}`}
                      className="w-full p-3 border border-gray-300 rounded-md text-center text-lg"
                      placeholder="0000"
                    />
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAadhaarMasked(!isAadhaarMasked)}
                className="absolute right-0 top-0 mt-3 mr-4 text-gray-600 hover:text-gray-800 focus:outline-none"
                aria-label={isAadhaarMasked ? "Unmask Aadhaar" : "Mask Aadhaar"}
              >
                {isAadhaarMasked ? <FaEye /> : <FaEyeSlash />}
              </button>
            </div>
          </>
        )}

        {/* Mobile Form */}
        {activeTab === "mobile" && (
          <>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Mobile Number<span className="text-red-500">*</span>
            </label>
            <div className="flex mb-6">
              <span className="inline-flex items-center px-4 py-3 border border-gray-300 bg-gray-50 text-gray-600 rounded-l-md">
                +91
              </span>
              <input
                type="text"
                placeholder="Enter your mobile number"
                value={mobileNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setMobileNumber(value.slice(0, 10));
                }}
                className="w-full p-3 border border-gray-300 rounded-r-md text-lg"
              />
            </div>
          </>
        )}

        {/* Terms and Conditions */}
        <TermsAndConditions
          isAgreed={isAgreed}
          setIsAgreed={setIsAgreed}
          captcha={captcha}
          handleCaptchaChange={handleCaptchaChange}
          captchaAnswer={captchaAnswer}
          isCaptchaCorrect={isCaptchaCorrect}
          handleSubmit={handleSendOtp}
          loading={loading}
          setCaptcha={setCaptcha}
          setCaptchaAnswer={setCaptchaAnswer}
          setIsCaptchaCorrect={setIsCaptchaCorrect}
          isAdhar={activeTab === "aadhaar"}
        />
      </div>

      {/* OTP Popup */}
      {showOtpPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative bg-white rounded-lg p-6 w-96">
            {/* Close Button */}
            <button
              onClick={() => setShowOtpPopup(false)}
              className="absolute top-2 right-2 text-gray-600 hover:text-red-500 text-xl font-bold"
            >
              &times;
            </button>

            <h3 className="text-lg font-bold mb-4">Enter OTP</h3>
            {otpMessage && (
              <p className="text-sm text-gray-600 mb-4">{otpMessage}</p>
            )}
            <div className="flex justify-center space-x-2 mb-4">
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, idx)}
                  onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                  className="w-10 h-12 p-2 border border-gray-300 rounded-md text-center text-lg"
                  placeholder="0"
                />
              ))}
            </div>
            <button
              onClick={
                activeTab === "mobile" ? verifyMobileOtp : handleVerifyOtp
              }
              disabled={loading || otp.some((digit) => digit === "")}
              className={`w-full py-3 rounded-md font-semibold mb-4 ${
                loading || otp.some((digit) => digit === "")
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                  : "bg-teal-600 text-white hover:bg-teal-700"
              }`}
            >
              {loading ? "Verifying..." : "Next"}
            </button>

            {resendAttempts < MAX_RESEND_ATTEMPTS ? (
              <p className="mt-2 text-sm text-gray-600 text-center">
                Didn’t receive OTP?{" "}
                <button
                  onClick={handleResendOtp}
                  disabled={
                    isResendDisabled || resendAttempts >= MAX_RESEND_ATTEMPTS
                  }
                  className={`font-medium ${
                    isResendDisabled || resendAttempts >= MAX_RESEND_ATTEMPTS
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-teal-600 hover:underline"
                  }`}
                >
                  Resend OTP
                </button>{" "}
                ({timer}s remaining) • {MAX_RESEND_ATTEMPTS - resendAttempts}{" "}
                attempts left
              </p>
            ) : (
              <div className="mt-2 text-center">
                <span className="text-red-500 font-medium">
                  Resend OTP exhausted
                </span>
              </div>
            )}

            {/* <p className="text-gray-600 text-sm mt-2">
              You have {MAX_RESEND_ATTEMPTS - resendAttempts} resend attempt
              {MAX_RESEND_ATTEMPTS - resendAttempts === 1 ? "" : "s"} remaining.
            </p> */}
          </div>
        </div>
      )}

      {/* Profile Selection Popup */}
      {showOtpPopup && profiles.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative bg-white rounded-lg p-6 w-96">
            {/* Close Button */}
            <button
              onClick={() => setShowOtpPopup(false)}
              className="absolute top-2 right-2 text-gray-600 hover:text-red-500 text-xl font-bold"
            >
              &times;
            </button>

            <h3 className="text-lg font-bold mb-4">Select a Profile</h3>
            {profiles.map((profile, index) => (
              <div
                key={index}
                className={`border p-4 rounded-md mb-2 cursor-pointer hover:bg-gray-100 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={() => {
                  if (!loading) {
                    fetchProfileDetails(profile.ABHANumber);
                  }
                }}
              >
                <p className="font-bold">{profile.name}</p>
                <p>Gender: {profile.gender}</p>
                <p>ABHA Number: {profile.ABHANumber}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {showNoAbhaPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold mb-4 text-red-600">
              No ABHA user registered
            </h3>
            <p className="text-gray-700 mb-6">
              No ABHA user registered with this Aadhaar number.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => cancelNoAbhaPopup()}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate("/healthacnt")}
                className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
              >
                Create ABHA
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

const TermsAndConditions = ({
  isAgreed,
  setIsAgreed,
  captcha,
  handleCaptchaChange,
  captchaAnswer,
  isCaptchaCorrect,
  handleSubmit,
  loading,
  setCaptcha,
  setCaptchaAnswer,
  setIsCaptchaCorrect,
  isAdhar,
}) => {
  const [checks, setChecks] = useState(Array(consentTexts.length).fill(false));

  useEffect(() => {
    setIsAgreed(checks.every(Boolean));
  }, [checks, setIsAgreed]);

  const allChecked = checks.every(Boolean);

 const currentUser = secureStorage.get("user", {}) ;
   const healthWorkerName = [
    currentUser.firstName,
    currentUser.middleName,
    currentUser.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const [beneficiaryName, setBeneficiaryName] = useState("");

  const dynamicConsentTexts = [
    `I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI, with National Health Authority (NHA) for the sole purpose of creation of ABHA number. I understand that my ABHA number can be used and shared for purposes as may be notified by ABDM from time to time including provision of healthcare services. Further, I am aware that my personal identifiable information (Name, Address, Age, Date of Birth, Gender and Photograph) may be made available to the entities working in the National Digital Health Ecosystem (NDHE) which inter alia includes stakeholders and entities such as healthcare professionals (e.g. doctors), facilities (e.g. hospitals, laboratories) and data fiduciaries (e.g. health programmes), which are registered with or linked to the Ayushman Bharat Digital Mission (ABDM), and various processes there under.`,
    `I authorize NHA to use my Aadhaar number for performing Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose. I understand that UIDAI will share my e-KYC details, or response of “Yes” with NHA upon successful authentication.`,
    `I consent to usage of my ABHA address and ABHA number for linking of my legacy (past) health records and those which will be generated during this encounter.`,
    `I authorize the sharing of all my health records with healthcare provider(s) for the purpose of providing healthcare services to me during this encounter.`,
    `I consent to the anonymization and subsequent use of my health records for public health purposes.`,
    `I, ${
      healthWorkerName || "(healthcare worker name)"
    } confirm that I have duly informed and explained the beneficiary of the contents of consent for aforementioned purposes.`,
    `I, ${
      beneficiaryName || "(beneficiary name)"
    } have been explained about the consent as stated above and hereby provide my consent for the aforementioned purposes.`,
  ];

  return (
    <div className="mb-6">
      {isAdhar && (
        <>
          {/* Declaration header */}
          <p className="text-sm font-medium text-black mb-2">
            I hereby declare that :
          </p>

          <div className="p-4 border-2 border-dashed border-gray-400 rounded-lg space-y-4">
            {dynamicConsentTexts.map((text, i) => {
              const isLast = i === dynamicConsentTexts.length - 1;
              return (
                <div key={i} className="flex items-start">
                  <button
                    type="button"
                    onClick={() => {
                      const c = [...checks];
                      c[i] = !c[i];
                      setChecks(c);
                    }}
                    className="mt-1 focus:outline-none"
                  >
                    {checks[i] ? (
                      <FaCheckSquare size={20} className="text-black" />
                    ) : (
                      <FaRegSquare size={20} className="text-black" />
                    )}
                  </button>
                  <div className="ml-3 flex-1 text-sm text-black leading-snug">
                    {isLast ? (
                      <>
                        I,&nbsp;
                        <input
                          type="text"
                          placeholder="Enter patient name"
                          value={beneficiaryName}
                          onChange={(e) => setBeneficiaryName(e.target.value)}
                          className="border-b border-gray-400 focus:outline-none px-1"
                        />
                        &nbsp;have been explained about the consent as stated
                        above and hereby provide my consent for the
                        aforementioned purposes.
                      </>
                    ) : (
                      text
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Captcha */}
      <div className="mb-6 mt-6">
        <label className="block text-sm font-medium text-black mb-2">
          Captcha<span className="text-red-500">*</span>
        </label>
        <div className="flex items-center space-x-4">
          <div className="w-32 h-12 flex items-center justify-center border border-gray-300 rounded-md text-2xl font-semibold">
            {captcha.question}
          </div>
          <input
            type="text"
            placeholder="Enter answer"
            value={captchaAnswer}
            onChange={(e) => handleCaptchaChange(e.target.value)}
            className="flex-1 p-3 border border-gray-300 rounded-md"
          />
          <button
            onClick={() => {
              const newCap = generateCaptcha();
              setCaptcha(newCap);
              setCaptchaAnswer("");
              setIsCaptchaCorrect(false);
            }}
            className="text-teal-600 hover:text-teal-800 text-xl"
            aria-label="Refresh Captcha"
          >
            &#x21bb;
          </button>
        </div>
      </div>

      {/* Continue button */}
      <button
        onClick={handleSubmit}
        disabled={
          loading || (isAdhar && (!allChecked || !beneficiaryName.trim()))
        }
        className={`w-full py-3 rounded-md text-lg font-semibold ${
          loading || (isAdhar && (!allChecked || !beneficiaryName.trim()))
            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
            : "bg-teal-600 text-white hover:bg-teal-700"
        }`}
      >
        {loading ? "Sending OTP..." : "Continue"}
      </button>
    </div>
  );
};

const generateCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10);
  const num2 = Math.floor(Math.random() * 10);
  return { question: `${num1} x ${num2}`, answer: (num1 * num2).toString() };
};

export default ForgotAbha;
